
import { PuttingResult, puttingTestNames } from "@/types/puttingResults";

/**
 * Formats putting test results to the common PuttingResult format
 */
export const formatPuttingResults = (results: any[]): PuttingResult[] => {
  console.log('formatPuttingResults - raw results count:', results.length);
  console.log('formatPuttingResults - situation_ids:', results.map(r => r.situation_id));
  
  return results.map(result => ({
    ...result,
    test_name: result.situation_id >= 7 && result.situation_id <= 15 
      ? "Test d'approche" 
      : "Test de putting standard"
  }));
};

/**
 * Formats level tests results to the common PuttingResult format
 */
export const formatLevelResults = (results: any[]): PuttingResult[] => {
  return results.map(result => ({
    id: result.id,
    user_id: result.user_id,
    situation_id: 0, // Use 0 for level tests
    points: result.points,
    index: result.index || 0,
    test_date: result.test_date,
    test_level: result.level,
    // Ensure we use the correct name from the mapping or fallback to the test_id
    test_name: result.test_id && puttingTestNames[result.test_id] 
      ? puttingTestNames[result.test_id] 
      : (result.test_name || result.test_id || "Entraînement"),
    metadata: result.metadata || {} // Ensure metadata is passed through
  }));
};

/**
 * Formats putting training results with test_name if missing
 */
export const formatPuttingTrainingResults = (results: any[]): PuttingResult[] => {
  return results.map(result => ({
    ...result,
    // Map test_id to a human-readable name if available, otherwise use what's already there
    test_name: result.test_id && puttingTestNames[result.test_id] 
      ? puttingTestNames[result.test_id] 
      : (result.test_name || "Entraînement putting"),
    metadata: result.metadata || {} // Ensure metadata is passed through
  }));
};

/**
 * Filter results by level
 */
export const filterByLevel = (results: PuttingResult[], level: string): PuttingResult[] => {
  // Assurons-nous que la comparaison est stricte et convertissons tout en chaîne
  return results.filter(r => String(r.test_level) === level);
};

/**
 * Filter results by test type (putting, swing, etc.)
 * Version améliorée avec gestion des accents et des identifiants connus
 */
export const filterByTestType = (results: PuttingResult[], testType: 'putting' | 'swing' | 'chipping' | 'wedging' | 'bunker'): PuttingResult[] => {
  // Définir des identifiants explicites par type de test
  const explicitTestIds: Record<string, string[]> = {
    putting: ['putts-plat', 'putts-montee', 'putts-descente', 'putts-pente', 'putts-pente-droite'],
    swing: ['iron-', 'fairwaywood-', 'driver-'],
    chipping: ['chipping-', 'chip-'],
    wedging: ['wedging-', 'wedg-'],
    bunker: ['bunker-']
  };
  
  // Définir des mots-clés pour recherche dans les noms
  const keywords: Record<string, string[]> = {
    putting: ['putt', 'plat', 'pente', 'montée', 'montee', 'descente'],
    swing: ['fer', 'bois', 'iron', 'fairwaywood', 'driver'],
    chipping: ['chip', 'roulée', 'roulee'],
    wedging: ['wedg', 'cercles', 'mid'],
    bunker: ['bunker', 'sable']
  };
  
  // Définir des mots-clés d'exclusion par type
  const exclusionKeywords: Record<string, string[]> = {
    putting: ['chip', 'wedg', 'bunker', 'iron', 'driv', 'bois', 'fairway'],
    swing: ['putt', 'chip', 'wedg', 'bunker'],
    chipping: ['putt', 'wedg', 'bunker', 'iron', 'driv', 'bois'],
    wedging: ['putt', 'chip', 'bunker', 'iron', 'driv', 'bois'],
    bunker: ['putt', 'chip', 'wedg', 'iron', 'driv', 'bois']
  };
  
  return results.filter(result => {
    // Normalisons tout en minuscules pour une comparaison cohérente
    const testName = (result.test_name || '').toLowerCase();
    const testId = (result.test_id || '').toLowerCase();
    
    // 1. Vérification des identifiants explicites
    if (explicitTestIds[testType]) {
      // Vérifier si c'est un ID exact
      if (explicitTestIds[testType].includes(testId)) {
        return true;
      }
      
      // Vérifier si ça commence par un ID connu
      for (const id of explicitTestIds[testType]) {
        if (testId.startsWith(id)) {
          return true;
        }
      }
    }
    
    // 2. Vérifications spécifiques au type
    if (testType === 'putting') {
      if (testId.startsWith('putts-')) {
        return true;
      }
    } else if (testType === 'swing') {
      // Retourne true uniquement pour iron- et fairwaywood- (pas driver-)
      if (testId.startsWith('iron-') || testId.startsWith('fairwaywood-')) {
        return true;
      }
      // Inclure driver uniquement si on est dans un contexte où on veut explicitement tous les types de swing
      if (testId.startsWith('driver-')) {
        return true; // On laisse au code appelant le soin de filtrer les driver si nécessaire
      }
    }
    
    // 3. Vérification par mots-clés, avec exclusions
    const hasKeyword = keywords[testType].some(keyword => 
      testName.includes(keyword) || testId.includes(keyword)
    );
    
    const hasExclusion = exclusionKeywords[testType].some(keyword => 
      testName.includes(keyword) || testId.includes(keyword)
    );
    
    return hasKeyword && !hasExclusion;
  });
};

/**
 * Filter results by test name or test ID
 * This is a helper to ensure we can find results by either property
 */
export const filterByTestName = (results: PuttingResult[], testName: string): PuttingResult[] => 
  results.filter(r => 
    r.test_name === testName || 
    r.test_id === testName ||
    // Handle special cases where test_id and test_name might not match directly
    (testName === "putts-pente-droite" && r.test_name === "Les Putts en Pente droite gauche") ||
    (testName === "Les Putts en Pente droite gauche" && r.test_id === "putts-pente-droite")
  );
